import React, { useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ReactComponent as BaseExclamationMarkIcon } from '@ubeya/shared-web/assets/exclamation-mark-outlined.svg';
import { ReactComponent as RoundGreyXIcon } from '@ubeya/shared-web/assets/compliance-no.svg';
import ConfigContext from '../../contexts/ConfigContext';
import usePortalHook from '../../hooks/usePortal';
import { media } from '../../style';
import BaseButton from '../Button';
import { card } from '../Card';
import CircleIcon from '../CircleIcon';
import { Flex, FlexCenter, FlexColumn, FlexMiddle } from '../Flex';
import { useModal } from '../Modal';
import { Body, body as cssBody, Header2SemiBold } from '../Typography';
import { ReactComponent as CloseIcon } from '../../assets/close-small.svg';

const Wrapper = styled(FlexCenter).attrs(() => ({ className: 'confirmation-modal-wrapper' }))`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background: rgba(19, 21, 41, 0.3);
  z-index: ${({ zIndex }) => zIndex || 6};

  ${({ confirmationModalWrapperStyle }) => confirmationModalWrapperStyle};
`;

const Button = styled(BaseButton)`
  min-width: 80px;
  padding: 6px 16px;
  border-radius: 4px;

  .button-loader {
    top: 8px;
    .lds-ring div {
      border-color: ${({ theme }) => theme.colors.white} transparent transparent transparent;
    }
  }

  &.confirmation-confirm-button:not(:disabled) {
    background-color: ${({ theme, $isPermanentAction }) =>
      $isPermanentAction ? theme.colors.redNegative : theme.colors.primary};
    &:hover:not(:disabled) {
      background-color: ${({ theme, $isPermanentAction }) =>
        $isPermanentAction ? theme.colors.redLight : theme.colors.primaryLight};
    }
  }

  &.confirmation-cancel-button {
    border: 1px solid ${({ theme }) => theme.colors.grey3};
    color: ${({ theme }) => theme.colors.black};
    &:hover:not(:disabled) {
      border: 1px solid ${({ theme }) => theme.colors.grey3};
      color: ${({ theme }) => theme.colors.grey3};
      box-shadow: none;
    }
  }
`;

const RoundedX = styled(RoundGreyXIcon)`
  path:first-of-type {
    fill: ${({ theme }) => theme.colors.redNegative};
  }
  width: 27px;
  height: 27px;
`;

const Container = styled(FlexColumn)`
  position: relative;
  ${card};
  width: 90vw;
  max-height: 60vh;
  padding: 24px;
  ${media.mobile`max-width: 500px;`}

  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);

  ${({ confirmationModalContainerStyle }) => confirmationModalContainerStyle};
`;

const TitleColumn = styled(Flex)`
  position: relative;
  justify-content: space-between;
`;

const Title = styled(Header2SemiBold).attrs(() => ({ className: 'title' }))`
  margin-bottom: 8px;
`;

const Content = styled(FlexColumn).attrs(() => ({ className: 'content' }))`
  flex: 1;
  ${cssBody};
  margin-bottom: 16px;
  overflow-y: auto;
`;

const WarningContainer = styled(Content)`
  padding: 12px;
  gap: 8px;
  margin: 0;
  border-radius: 4px;
  background-color: ${({ theme, $warningBgColor }) => theme.colors[$warningBgColor]};
`;

const ExclamationMarkIcon = styled(BaseExclamationMarkIcon)`
  width: 24px;
  height: 24px;
`;

const WarningRow = styled(Flex).attrs(() => ({ className: 'warning-row' }))`
  gap: 8px;
  align-items: center;
`;

const Buttons = styled(FlexMiddle)`
  justify-content: flex-start;
  flex-direction: row-reverse;
  gap: 10px;
  margin-top: 16px;

  .confirmation-confirm-button {
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.primaryLight};
    }
  }
`;

const CloseButton = styled(CircleIcon)`
  position: absolute;
  right: -10px;
  top: -10px;
`;

const ConfirmationModal = ({
  children,
  onClose,
  title,
  body,
  confirmText = 'update',
  hasCloseButton = false,
  onConfirm,
  showExtraButton,
  loadingExtraButton,
  onExtraButtonClick,
  extraButtonText = '',
  disabledConfirm = false,
  showConfirmButton = true,
  loadingConfirm = false,
  cancelText = 'cancel',
  closeOnClickOutside = false,
  onCancelClick = undefined,
  className,
  renderButtons,
  usePortal,
  showCancelButton = true,
  wrapperClassName,
  confirmationModalContainerStyle,
  confirmationModalWrapperStyle,
  dataTestId,
  zIndex,
  warnings = [],
  errors = [],
  warningBgColor = 'redLight',
  isPermanentAction = false
}) => {
  const { t } = useContext(ConfigContext);

  const container = useRef();

  const { Portal } = usePortalHook({
    enabled: usePortal,
    closeOnClickOutside: false,
    portalId: 'modal'
  });

  const { attachModal, detachModal } = useModal();
  useEffect(() => {
    attachModal({ onClose });

    return () => {
      detachModal({ onClose });
    };
    // ignore onClose as in most cases it's not memoized and causes infinite rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachModal, detachModal]);

  return (
    <Portal>
      <Wrapper
        onClick={() => closeOnClickOutside && onClose()}
        data-modal="confirmationModal"
        className={wrapperClassName}
        confirmationModalWrapperStyle={confirmationModalWrapperStyle}
        zIndex={zIndex}>
        <Container
          confirmationModalContainerStyle={confirmationModalContainerStyle}
          ref={container}
          className={className}
          onClick={(e) => e.stopPropagation()}
          data-testid={dataTestId}>
          <TitleColumn>
            <Title>{errors.length ? t('popUpUnableToCompleteAction') : t(title)}</Title>
            {hasCloseButton && <CloseButton onClick={onClose} icon={CloseIcon} />}
          </TitleColumn>

          {errors.length > 0 ? (
            <WarningContainer>
              {errors.map((error, index) => (
                <WarningRow key={index + error}>
                  <RoundedX />
                  <Body>{error}</Body>
                </WarningRow>
              ))}
            </WarningContainer>
          ) : (
            (body || children) && <Content>{body ? t(body) : children}</Content>
          )}

          {warnings.length > 0 && (
            <WarningContainer $warningBgColor={warningBgColor}>
              {warnings.map((warning, index) => (
                <WarningRow key={index + warning}>
                  <ExclamationMarkIcon />
                  <Body>{warning}</Body>
                </WarningRow>
              ))}
            </WarningContainer>
          )}

          <Buttons>
            {errors.length === 0 && showConfirmButton && (
              <Button
                primary
                type="button"
                disabled={disabledConfirm}
                isLoading={loadingConfirm}
                onClick={onConfirm}
                className="confirmation-confirm-button"
                autoFocus
                $isPermanentAction={isPermanentAction}
                dataTestId="confirm-button">
                {t(confirmText)}
              </Button>
            )}

            {renderButtons?.({ loadingConfirm })}

            {showExtraButton && (
              <Button secondary type="button" isLoading={loadingExtraButton} onClick={onExtraButtonClick}>
                {t(extraButtonText)}
              </Button>
            )}

            {showCancelButton && (
              <Button secondary type="button" className="confirmation-cancel-button" onClick={onCancelClick || onClose}>
                {t(cancelText)}
              </Button>
            )}
          </Buttons>
        </Container>
      </Wrapper>
    </Portal>
  );
};

export default ConfirmationModal;
